import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { BiLockAlt } from 'react-icons/bi';
import PageHeader from 'Utils/PageHeader';
import FetchContainer from 'Utils/FetchContainer';
import { getLicenseStatus } from 'License/api';

export const TDM_FEATURE = 'tdm';

/** { licensed, expired, expiresAt } from the platform license status shape { features: { tdm: { expiresAt, valid } } }. */
export const tdmLicenseState = (status) => {
  const f = status && status.features ? status.features[TDM_FEATURE] : null;
  if (!f) return { licensed: false, expired: false, expiresAt: null };
  return { licensed: Boolean(f.valid), expired: !f.valid && Boolean(f.expiresAt), expiresAt: f.expiresAt || null };
};

/** A 403 LICENSE_REQUIRED / LICENSE_EXPIRED from any TDM call (ApiError.body). */
export const isTdmLicenseError = (e) => {
  const body = e && e.body;
  return Boolean(e && Number(e.status) === 403 && body && (body.error === 'LICENSE_REQUIRED' || body.error === 'LICENSE_EXPIRED'));
};

export const NotLicensedPanel = ({ expired, expiresAt }) => {
  const history = useHistory();
  return (
    <div className='tdm-page tdm-page--narrow' data-testid='tdm-not-licensed'>
      <PageHeader
        icon={BiLockAlt}
        eyebrow='Test Data Management'
        header={expired ? 'Test Data Management license expired' : 'Test Data Management is not licensed'}
        tooltip='Licensed feature: tdm'
        meta={<span>{expired
          ? `The "tdm" feature of your platform license expired on ${expiresAt}.`
          : 'Your platform license does not include the "tdm" feature.'}</span>}
      />
      <div className='tdm-section'>
        <div className='tdm-section__title'>What this unlocks</div>
        <div className='tdm-section__sub'>
          Source groups, referentially closed subsets, staging areas, subset runs with masking, and test-environment
          deployment. The Data Graph stays available under your discover license.
        </div>
        <div className='tdm-actions'>
          <Button size='sm' onClick={() => history.push('/admin')} data-testid='tdm-goto-license'>Apply a license key</Button>
          <span className='tdm-note'>Ask your C2 contact for a key that includes the <code>tdm</code> feature.</span>
        </div>
      </div>
    </div>
  );
};

/**
 * Wraps the TDM routes: reads the platform license once and shows the not-licensed panel instead of
 * the screens (which would otherwise each fail with a 403) when the `tdm` feature is missing or
 * expired. Trial licenses cover every feature, so this only bites MANUAL keys issued without it.
 */
const TdmLicenseGate = ({ children }) => {
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    let alive = true;
    getLicenseStatus()
      .then((s) => { if (alive) setState(tdmLicenseState(s)); })
      .catch((e) => { if (alive) { setError(e?.message || 'Could not read the platform license'); setState({ licensed: false }); } });
    return () => { alive = false; };
  }, []);
  if (state == null) return <FetchContainer fetchingData={true} absolute={true} />;
  if (error) return <div className='tdm-page'><div className='tdm-error'>{error}</div></div>;
  if (!state.licensed) return <NotLicensedPanel expired={state.expired} expiresAt={state.expiresAt} />;
  return children;
};

export default TdmLicenseGate;
